import { useCallback, useEffect, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material'
import ExpandMore from '@mui/icons-material/ExpandMore'
import HistoryIcon from '@mui/icons-material/History'
import { getSupportPractitionerAccessLog } from 'src/supportPortal/services/practitionerOnboardingService'
import PractitionerAccessLogList from 'src/supportPortal/components/common/mentor/PractitionerAccessLogList'

/**
 * Collapsible "Access Log" section for a single practitioner — the enable/disable audit
 * trail. Drop it on the practitioner detail page; bump `refreshKey` after an enable/disable
 * so it refetches.
 */
export default function PractitionerAccessLogSection({
  practitionerId,
  practitionerSlug,
  refreshKey = 0,
  defaultExpanded = false,
}) {
  const [state, setState] = useState({ loading: true, error: '', logs: [] })

  const load = useCallback(async () => {
    if (!practitionerId && !practitionerSlug) {
      setState({ loading: false, error: '', logs: [] })
      return
    }
    setState((prev) => ({ ...prev, loading: true, error: '' }))
    const resp = await getSupportPractitionerAccessLog({ practitionerId, practitionerSlug })
    if (resp.ok) {
      setState({ loading: false, error: '', logs: resp.data || [] })
    } else {
      setState({
        loading: false,
        error: resp.message || 'Unable to load access log.',
        logs: [],
      })
    }
  }, [practitionerId, practitionerSlug])

  useEffect(() => {
    load()
  }, [load, refreshKey])

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{
        width: '100%',
        maxWidth: '1400px',
        margin: '0 auto',
        borderRadius: '16px !important',
        border: '1px solid rgba(0, 0, 0, 0.06)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        background: 'rgba(255, 255, 255, 0.95)',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2.5, minHeight: 64 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <HistoryIcon sx={{ color: 'rgba(216, 101, 0, 0.8)' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
            Access Log
          </Typography>
          {!state.loading && !state.error && (
            <Chip size="small" label={`${state.logs.length} entr${state.logs.length === 1 ? 'y' : 'ies'}`} />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Every enable / disable action on this practitioner, newest first — who did it, when,
          and why.
        </Typography>
        {state.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : state.error ? (
          <Alert severity="error">{state.error}</Alert>
        ) : (
          <PractitionerAccessLogList logs={state.logs} />
        )}
      </AccordionDetails>
    </Accordion>
  )
}
